import { Link } from "react-router-dom";
import { HeartHandshake } from "lucide-react";

const SECTIONS = [
  {
    title: "Who we are (data controller)",
    body: "RoutineBuddy is operated by Simplerlivez LTD, which is the data controller for individual accounts. For organisation accounts, the care organisation is the data controller for its residents' data and Simplerlivez LTD acts as data processor. Contact for all privacy matters: hello@simplerlivez.com.",
  },
  {
    title: "What we store",
    body: "Account details (name, email, bcrypt-hashed password), and the content you create: profiles of people you support (first names only recommended), routines, communication boards, choice boards, care notes with optional photos and voice recordings, uploaded care plan documents, daily progress, and mood check-ins. Photos, voice notes and care plan documents are stored in encrypted object storage. We never sell data or use it for advertising.",
  },
  {
    title: "Why we process it (lawful bases)",
    body: "We process account and app content to provide the service you signed up for (contract). Care notes and profiles may include health-adjacent information about the person supported; this special-category data is processed because you (or the care organisation) choose to record it for the person's care, under explicit consent and/or the provision of health or social care. Product analytics are processed under legitimate interest and are privacy-friendly (no advertising profiles).",
  },
  {
    title: "Who can see it",
    body: "Individual accounts: only you, plus anyone you add through Care Circle sharing (you can remove them anytime). Organisation accounts: the organisation's owner and invited staff. Public share links (routines, boards, status pages) are viewable by anyone with the link — share them carefully; deleting the routine or board revokes access.",
  },
  {
    title: "AI features",
    body: "Paid-plan AI features (routine generation, smart sentences, picture generation, care plan summaries, camera-to-routine) send the text or images you provide to an AI provider to generate a response. Do not enter full names, NHS numbers or other highly sensitive identifiers into AI prompts.",
  },
  {
    title: "Service providers (sub-processors)",
    body: "We use: cloud hosting and database (MongoDB) to store data; Stripe for payments (we never see your card details); an email delivery service for digest, reset and notification emails; and an AI provider for the AI features above. Each processes data only to provide its service to us.",
  },
  {
    title: "Emails we send",
    body: "We send service emails: password resets, daily Care Circle digests, weekly organisation summaries, trial-ending reminders, re-engagement nudges and referral rewards. All except password resets can be turned off in Dashboard settings.",
  },
  {
    title: "Cookies & analytics",
    body: "We use essential cookies to keep you signed in (required for the app to work) and privacy-friendly product analytics. No advertising trackers, ever.",
  },
  {
    title: "How long we keep it",
    body: "Data is kept while your account is active. Deleted accounts and their content are removed immediately (backups may retain encrypted copies briefly). Password reset tokens expire after 1 hour. You can delete individual notes, plans and profiles at any time.",
  },
  {
    title: "Your rights (UK GDPR)",
    body: "You have the right to access, correct, export, restrict or delete your data, and to object to processing. Account deletion is self-serve (Carer dashboard → Your data → Delete account) and permanently removes your profiles, routines, boards, notes, care plans and progress. Organisation owners must remove staff first, which closes the organisation and deletes its data. For anything else, email hello@simplerlivez.com — we respond within 30 days. You can also complain to the Information Commissioner's Office (ico.org.uk).",
  },
  {
    title: "Security",
    body: "Passwords are hashed with bcrypt, sessions use encrypted cookies, all traffic is encrypted in transit (HTTPS), and uploaded files are stored in access-controlled object storage. RoutineBuddy is not yet formally certified (ISO 27001 / SOC 2); if your organisation requires certified infrastructure, contact us before storing sensitive personal data.",
  },
];

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-background">
      <header className="max-w-3xl mx-auto px-4 py-4">
        <Link to="/" data-testid="privacy-home-link" className="flex items-center gap-2 w-fit">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Privacy & your data</h1>
        <p className="mt-2 text-lg text-muted-foreground">Last updated: 2026. Written in plain English on purpose.</p>
        <div className="mt-8 space-y-6">
          {SECTIONS.map((s) => (
            <section key={s.title} className="bg-white rounded-3xl border-2 border-foreground/10 p-6">
              <h2 className="text-xl font-extrabold">{s.title}</h2>
              <p className="mt-2 text-lg leading-relaxed text-muted-foreground">{s.body}</p>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
